import React, { useMemo, useState } from 'react';
import { Plus, Trash2, Calculator, Download, Lock, Printer, Link2, Sparkles, X, AlertTriangle } from 'lucide-react';

/**
 * Budget planner — shared by AD Finance ▸ Budget and the member link page (/budget/:token)
 * so the two never drift (docs/AD_DASHBOARD_AND_FINANCE.md §2b). The concept is the club's
 * planning workbook: line items by FISCAL QUARTER, CALCULATORS that drive lines from per-team
 * figures (qty × (unit + add-on), spread by a quarter split), a summary with break-even fee per
 * player and a reserve goal, and a board-approved RESOLUTION.
 * Controlled: `budget` in, `onChange(patch)` out. The backend recomputes the same way on save.
 */
export const money = (n) => (Number(n) || 0).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
const money0 = (n) => (Number(n) || 0).toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const uid = () => Math.random().toString(36).slice(2, 14);
const num = (x) => Number(x) || 0;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Jul–Sep" … from the fiscal-year start month. */
export const quarterLabels = (start = 1) => [0, 1, 2, 3].map((q) => {
  const a = (start - 1 + q * 3) % 12;
  return `${MONTHS[a]}–${MONTHS[(a + 2) % 12]}`;
});

const four = (v) => [0, 1, 2, 3].map((i) => num((v || [])[i]));
export const calcTotal = (c) => (c.rows || []).filter((r) => r.include !== false)
  .reduce((a, r) => a + num(r.qty) * (num(r.unit) + num(c.addon)), 0);

/** Same rule as the backend `_apply_calcs`: each calculator's total → its line, by split %. */
export function applyCalcs(sections, calcs) {
  const fed = {};
  (calcs || []).forEach((c) => { if (c.line_id && !fed[c.line_id]) fed[c.line_id] = c; });
  return (sections || []).map((s) => ({
    ...s,
    lines: (s.lines || []).map((l) => {
      const c = fed[l.id];
      if (!c) return l.calc_id ? { ...l, calc_id: null } : l;
      const total = Math.round(calcTotal(c) * 100) / 100;
      let split = four(c.split).map((x) => Math.max(0, x));
      if (split.reduce((a, b) => a + b, 0) <= 0) split = [100, 0, 0, 0];
      const sum = split.reduce((a, b) => a + b, 0);
      const amts = split.map((x) => Math.round((total * x / sum) * 100) / 100);
      amts[3] = Math.round((amts[3] + total - amts.reduce((a, b) => a + b, 0)) * 100) / 100;
      return { ...l, amounts: amts, calc_id: c.id };
    }),
  }));
}

export function budgetTotals(b) {
  const t = { income_q: [0, 0, 0, 0], expense_q: [0, 0, 0, 0], income_actual: 0, expense_actual: 0, income_prior: 0, expense_prior: 0 };
  (b.sections || []).forEach((s) => (s.lines || []).forEach((l) => {
    four(l.amounts).forEach((a, i) => { t[`${s.kind}_q`][i] += a; });
    t[`${s.kind}_actual`] += num(l.actual); t[`${s.kind}_prior`] += num(l.prior_actual);
  }));
  t.balance_q = t.income_q.map((x, i) => x - t.expense_q[i]);
  t.income_planned = t.income_q.reduce((a, b2) => a + b2, 0);
  t.expense_planned = t.expense_q.reduce((a, b2) => a + b2, 0);
  t.net_planned = t.income_planned - t.expense_planned;
  t.net_actual = t.income_actual - t.expense_actual;
  t.net_prior = t.income_prior - t.expense_prior;
  t.variance = t.net_actual - t.net_planned;
  t.alerts = (b.sections || []).reduce((a, s) => a + (s.lines || []).filter((l) => l.alert).length, 0);
  const pct = num(b.settings?.reserve_pct);
  t.reserve_pct = pct; t.reserve_goal = t.income_planned * pct / 100; t.reserve_gap = t.net_planned - t.reserve_goal;
  const pcs = (b.calculators || []).filter((c) => c.source === 'team_players');
  t.players = pcs.reduce((a, c) => a + (c.rows || []).filter((r) => r.include !== false).reduce((x, r) => x + num(r.qty), 0), 0);
  const feeLines = new Set(pcs.map((c) => c.line_id));
  t.season_fees = (b.sections || []).flatMap((s) => s.lines || []).filter((l) => feeLines.has(l.id))
    .reduce((a, l) => a + four(l.amounts).reduce((x, y) => x + y, 0), 0);
  t.break_even_fee = t.players ? (t.expense_planned - (t.income_planned - t.season_fees)) / t.players : null;
  t.fundraising_goal_total = pcs.reduce((a, c) => a + (c.rows || []).filter((r) => r.include !== false).reduce((x, r) => x + num(r.qty) * num(c.addon), 0), 0);
  return t;
}

/** The resolution with {org} {season} {version} {reserve_pct} filled in. */
export const renderResolution = (b, org) => (b.resolution || '')
  .replaceAll('{org}', org || '').replaceAll('{season}', b.season || '')
  .replaceAll('{version}', String(b.version || 1)).replaceAll('{reserve_pct}', String(num(b.settings?.reserve_pct)));

const cell = 'w-full px-1 py-0.5 text-xs bg-transparent border border-transparent rounded hover:border-slate-300 focus:border-blue-500 focus:outline-none dark:hover:border-slate-600 text-slate-900 dark:text-slate-100 disabled:hover:border-transparent';
const th = 'px-1.5 py-1 font-medium text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400';
const pos = (v) => (v < -0.004 ? 'text-red-600 dark:text-red-400' : 'text-slate-900 dark:text-slate-100');

export default function BudgetPlanner({ budget, onChange, member = false, orgName = '', onFill, onPrint, onLoadAccounts, onSaveLinks }) {
  const [tab, setTab] = useState('summary');
  const locked = ['submitted', 'approved'].includes(budget.status);
  const ro = locked;
  const ql = quarterLabels(budget.settings?.fiscal_start_month || 1);
  const t = useMemo(() => budgetTotals(budget), [budget]);
  const sections = budget.sections || [];
  const calcs = budget.calculators || [];

  const setSections = (s) => onChange({ sections: applyCalcs(s, calcs) });
  const setCalcs = (c) => onChange({ calculators: c, sections: applyCalcs(sections, c) });

  const tabs = [['summary', 'Summary'], ['lines', 'Line items'], ['calcs', `Calculators (${calcs.length})`],
    ...(onLoadAccounts ? [['books', `Actuals vs budget${t.alerts ? ` (${t.alerts} flagged)` : ''}`]] : []), ['resolution', 'Resolution']];
  return (
    <div className="text-sm" data-testid="budget-planner">
      <div className="flex items-center gap-1 mb-2 border-b border-slate-200 dark:border-slate-700">
        {tabs.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={`budget-tab-${k}`}
            className={`px-3 py-1.5 text-xs font-medium -mb-px border-b-2 ${tab === k ? 'border-blue-600 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'}`}>{l}</button>
        ))}
        {locked && <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-amber-700 dark:text-amber-400"><Lock className="h-3 w-3" /> {budget.status} — locked</span>}
      </div>
      {tab === 'summary' && <Summary b={budget} t={t} ql={ql} />}
      {tab === 'lines' && <Lines sections={sections} calcs={calcs} ql={ql} ro={ro} onChange={setSections} goCalcs={() => setTab('calcs')} />}
      {tab === 'calcs' && <Calcs calcs={calcs} sections={sections} ql={ql} ro={ro} onChange={setCalcs} onFill={member ? null : onFill} />}
      {tab === 'books' && onLoadAccounts && <Books b={budget} ql={ql} onLoadAccounts={onLoadAccounts} onSaveLinks={onSaveLinks} />}
      {tab === 'resolution' && <Resolution b={budget} orgName={orgName} ro={ro || member} onChange={onChange} onPrint={onPrint} />}
    </div>
  );
}

// ── Summary ─────────────────────────────────────────────────────────────────
function Summary({ b, t, ql }) {
  const sub = (s) => [0, 1, 2, 3].map((i) => (s.lines || []).reduce((a, l) => a + four(l.amounts)[i], 0));
  const prior = (s) => (s.lines || []).reduce((a, l) => a + num(l.prior_actual), 0);
  const row = (label, q, total, last, cls = '', key) => (
    <tr key={key || label} className={`border-t border-slate-100 dark:border-slate-800 ${cls}`}>
      <td className="px-2 py-0.5">{label}</td>
      {q.map((x, i) => <td key={i} className={`px-1.5 text-right tabular-nums ${pos(x)}`}>{money0(x)}</td>)}
      <td className={`px-1.5 text-right tabular-nums font-semibold ${pos(total)}`}>{money0(total)}</td>
      <td className="px-1.5 text-right tabular-nums text-slate-500">{last ? money0(last) : '—'}</td>
    </tr>
  );
  const kinds = [['income', 'Income'], ['expense', 'Expenses']];
  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_260px] gap-3" data-testid="budget-summary">
      <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-auto max-h-[calc(100vh-360px)]">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800"><tr>
            <th className={`${th} text-left`}>Summary</th>{ql.map((q) => <th key={q} className={`${th} text-right`}>{q}</th>)}
            <th className={`${th} text-right`}>Total</th><th className={`${th} text-right`}>Last year</th></tr></thead>
          <tbody className="text-slate-800 dark:text-slate-200">
            {kinds.map(([k, label]) => (
              <React.Fragment key={k}>
                {(b.sections || []).filter((s) => s.kind === k).map((s) => row(s.name, sub(s), sub(s).reduce((a, x) => a + x, 0), prior(s), 'text-slate-600 dark:text-slate-400', s.id))}
                {row(`Total ${label.toLowerCase()}`, t[`${k}_q`], t[`${k}_planned`], t[`${k}_prior`], 'font-semibold bg-slate-50/60 dark:bg-slate-800/40')}
              </React.Fragment>
            ))}
            {row('Balance', t.balance_q, t.net_planned, t.net_prior, 'font-bold border-t-2 border-slate-300 dark:border-slate-600')}
          </tbody>
        </table>
      </div>
      <div className="space-y-2 text-xs" data-testid="budget-kpis">
        {[['Break-even fee per player', t.break_even_fee == null ? 'add players in the Player fees calculator' : money(t.break_even_fee),
          `What each of ${t.players} players would pay if season fees covered every expense other income does not.`],
          [`Reserve goal (${t.reserve_pct}% of revenue)`, money0(t.reserve_goal),
            `Projected balance ${money0(t.net_planned)} — ${t.reserve_gap >= 0 ? `${money0(t.reserve_gap)} above` : `${money0(-t.reserve_gap)} short of`} the goal.`],
          ['Fundraising goal (players × goal)', money0(t.fundraising_goal_total), 'Included in player season fees.'],
          ['Actual so far', money0(t.net_actual), `Income ${money0(t.income_actual)} · expenses ${money0(t.expense_actual)} · variance ${money0(t.variance)}`],
        ].map(([l, v, d]) => (
          <div key={l} className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">{l}</div>
            <div className="text-base font-semibold tabular-nums text-slate-900 dark:text-white">{v}</div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400">{d}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Line items ──────────────────────────────────────────────────────────────
function Lines({ sections, calcs, ql, ro, onChange, goCalcs }) {
  const calcName = Object.fromEntries(calcs.map((c) => [c.id, c.name]));
  const set = (si, fn) => onChange(sections.map((s, i) => (i === si ? fn(s) : s)));
  const setLine = (si, li, patch) => set(si, (s) => ({ ...s, lines: s.lines.map((l, j) => (j === li ? { ...l, ...patch } : l)) }));
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-auto max-h-[calc(100vh-360px)]" data-testid="budget-lines">
      <table className="w-full text-xs">
        <thead className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-800"><tr>
          <th className={`${th} text-left`}>Line item</th>{ql.map((q) => <th key={q} className={`${th} text-right w-20`}>{q}</th>)}
          <th className={`${th} text-right w-20`}>Total</th><th className={`${th} text-right w-20`}>Actual</th>
          <th className={`${th} text-right w-20`}>Last year</th><th className={`${th} text-left`}>Notes</th><th className="w-6" /></tr></thead>
        <tbody>
          {sections.map((s, si) => {
            const total = (s.lines || []).reduce((a, l) => a + four(l.amounts).reduce((x, y) => x + y, 0), 0);
            return (
              <React.Fragment key={s.id || si}>
                <tr className={s.kind === 'income' ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-amber-50 dark:bg-amber-900/20'}>
                  <td className="px-1 py-0.5" colSpan={5}>
                    <div className="flex items-center gap-1">
                      <input value={s.name} disabled={ro} onChange={(e) => set(si, (x) => ({ ...x, name: e.target.value }))} className={`${cell} font-semibold max-w-[240px]`} data-testid={`budget-section-name-${si}`} />
                      <select value={s.kind} disabled={ro} onChange={(e) => set(si, (x) => ({ ...x, kind: e.target.value }))}
                        className="text-[11px] rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 px-1">
                        <option value="income">Income</option><option value="expense">Expense</option>
                      </select>
                    </div>
                  </td>
                  <td className="px-1.5 text-right tabular-nums font-semibold text-slate-800 dark:text-slate-100">{money0(total)}</td>
                  <td colSpan={3} className="px-1">
                    {!ro && <button type="button" data-testid={`budget-add-line-${si}`} className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:underline"
                      onClick={() => set(si, (x) => ({ ...x, lines: [...(x.lines || []), { id: uid(), name: '', amounts: [0, 0, 0, 0], actual: 0, prior_actual: 0, notes: '' }] }))}>
                      <Plus className="h-3 w-3" /> Add line</button>}
                  </td>
                  <td className="text-center">{!ro && <button type="button" title="Remove section" className="text-slate-400 hover:text-red-600"
                    onClick={() => { if (window.confirm(`Remove the section “${s.name}” and its lines?`)) onChange(sections.filter((_, i) => i !== si)); }}><Trash2 className="h-3.5 w-3.5" /></button>}</td>
                </tr>
                {(s.lines || []).map((l, li) => {
                  const a = four(l.amounts);
                  const fedBy = l.calc_id && calcName[l.calc_id];
                  return (
                    <tr key={l.id || li} className="border-t border-slate-100 dark:border-slate-800">
                      <td className="px-1 pl-3"><div className="flex items-center gap-1">
                        <input value={l.name} disabled={ro} placeholder="Line item" onChange={(e) => setLine(si, li, { name: e.target.value })} className={cell} />
                        {fedBy && <button type="button" onClick={goCalcs} title={`From the calculator “${fedBy}”`} className="text-blue-500 shrink-0"><Calculator className="h-3.5 w-3.5" /></button>}
                      </div></td>
                      {a.map((x, i) => (
                        <td key={i} className="px-0.5">
                          <input type="number" step="0.01" value={x || ''} placeholder="0" disabled={ro || !!fedBy}
                            onChange={(e) => { const n = [...a]; n[i] = e.target.value; setLine(si, li, { amounts: n }); }}
                            className={`${cell} text-right ${fedBy ? 'text-blue-700 dark:text-blue-300' : ''}`} />
                        </td>
                      ))}
                      <td className="px-1.5 text-right tabular-nums font-medium text-slate-800 dark:text-slate-100">{money0(a.reduce((p, q) => p + q, 0))}</td>
                      <td className="px-0.5">{l.actual_source === 'books'
                        ? <span title="From OL Finance (linked accounts)" className={`flex items-center justify-end gap-0.5 px-1 tabular-nums ${l.alert ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-emerald-700 dark:text-emerald-300'}`}><Link2 className="h-3 w-3" />{money0(l.actual)}</span>
                        : <input type="number" step="0.01" value={l.actual || ''} placeholder="0" disabled={ro} onChange={(e) => setLine(si, li, { actual: e.target.value })} className={`${cell} text-right`} />}</td>
                      <td className="px-0.5"><input type="number" step="0.01" value={l.prior_actual || ''} placeholder="0" disabled={ro} onChange={(e) => setLine(si, li, { prior_actual: e.target.value })} className={`${cell} text-right`} /></td>
                      <td className="px-0.5"><input value={l.notes || ''} disabled={ro} onChange={(e) => setLine(si, li, { notes: e.target.value })} className={cell} /></td>
                      <td className="text-center">{!ro && <button type="button" title="Remove line" className="text-slate-400 hover:text-red-600"
                        onClick={() => set(si, (x) => ({ ...x, lines: x.lines.filter((_, j) => j !== li) }))}><Trash2 className="h-3.5 w-3.5" /></button>}</td>
                    </tr>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      {!ro && <button type="button" data-testid="budget-add-section" className="m-2 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline"
        onClick={() => onChange([...sections, { id: uid(), name: 'New section', kind: 'expense', lines: [] }])}><Plus className="h-3 w-3" /> Add section</button>}
    </div>
  );
}

// ── Calculators ─────────────────────────────────────────────────────────────
const SOURCES = { '': 'Typed in', team_players: 'Teams — players × team fee', team_home_games: 'Teams — home games', teams: 'Teams', team_coaches: 'Teams — one coach each' };

function Calcs({ calcs, sections, ql, ro, onChange, onFill }) {
  const [busy, setBusy] = useState('');
  const set = (ci, patch) => onChange(calcs.map((c, i) => (i === ci ? { ...c, ...patch } : c)));
  const setRow = (ci, ri, patch) => set(ci, { rows: calcs[ci].rows.map((r, j) => (j === ri ? { ...r, ...patch } : r)) });
  const lineOpts = sections.flatMap((s) => (s.lines || []).map((l) => ({ id: l.id, label: `${s.name} ▸ ${l.name || '(unnamed)'}` })));
  const fill = async (ci) => {
    const c = calcs[ci];
    if (c.rows.length && !window.confirm('Replace the rows with OL\'s teams for this season? Costs you typed are kept for teams with the same name.')) return;
    setBusy(c.id);
    try {
      const rows = await onFill(c.source);
      const old = Object.fromEntries((c.rows || []).map((r) => [r.label, r]));
      set(ci, { rows: rows.map((r) => ({ id: uid(), label: r.label, qty: r.qty, unit: num(r.unit) || num(old[r.label]?.unit), include: old[r.label]?.include !== false })) });
    } finally { setBusy(''); }
  };
  return (
    <div className="max-h-[calc(100vh-360px)] overflow-auto pr-1" data-testid="budget-calcs">
      <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-2">Each calculator works out one line item: the included rows' quantity × (cost + add-on), spread over the quarters by the split. Change a figure here and the line, the summary and the break-even fee follow.</p>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-2">
        {calcs.map((c, ci) => {
          const total = calcTotal(c);
          return (
            <div key={c.id || ci} className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40 p-2" data-testid={`calc-${ci}`}>
              <div className="flex items-center gap-1 mb-1">
                <Calculator className="h-4 w-4 text-blue-500 shrink-0" />
                <input value={c.name} disabled={ro} onChange={(e) => set(ci, { name: e.target.value })} className={`${cell} font-semibold text-sm`} />
                <span className="ml-auto tabular-nums font-semibold text-slate-900 dark:text-white whitespace-nowrap">{money0(total)}</span>
                {!ro && <button type="button" title="Remove calculator" className="text-slate-400 hover:text-red-600" onClick={() => { if (window.confirm(`Remove the calculator “${c.name}”? Its line keeps its last amounts.`)) onChange(calcs.filter((_, i) => i !== ci)); }}><Trash2 className="h-3.5 w-3.5" /></button>}
              </div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-slate-600 dark:text-slate-400 mb-1">
                <label className="flex items-center gap-1">Feeds
                  <select value={c.line_id || ''} disabled={ro} onChange={(e) => set(ci, { line_id: e.target.value || null })} className="max-w-[220px] rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-1 text-[11px] text-slate-800 dark:text-slate-200">
                    <option value="">— no line —</option>{lineOpts.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select></label>
                <span className="flex items-center gap-0.5">Split %
                  {ql.map((q, i) => <input key={q} type="number" min="0" title={q} value={four(c.split)[i]} disabled={ro}
                    onChange={(e) => { const s = four(c.split); s[i] = e.target.value; set(ci, { split: s }); }}
                    className="w-10 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-0.5 text-right text-[11px] text-slate-800 dark:text-slate-200" />)}
                </span>
                {c.addon_label !== undefined && c.addon_label !== '' && (
                  <label className="flex items-center gap-1">{c.addon_label}
                    <input type="number" value={c.addon || ''} placeholder="0" disabled={ro} onChange={(e) => set(ci, { addon: e.target.value })}
                      className="w-16 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-1 text-right text-[11px] text-slate-800 dark:text-slate-200" /></label>
                )}
                <label className="flex items-center gap-1">Rows from
                  <select value={c.source || ''} disabled={ro} onChange={(e) => set(ci, { source: e.target.value })} className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-1 text-[11px] text-slate-800 dark:text-slate-200">
                    {Object.entries(SOURCES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select></label>
                {c.source && onFill && !ro && (
                  <button type="button" onClick={() => fill(ci)} disabled={busy === c.id} data-testid={`calc-fill-${ci}`}
                    className="inline-flex items-center gap-0.5 font-medium text-blue-600 hover:underline disabled:opacity-50"><Download className="h-3 w-3" /> Fill from OL</button>
                )}
              </div>
              <table className="w-full text-xs">
                <thead><tr><th className="w-5" /><th className={`${th} text-left`}>Item</th><th className={`${th} text-right w-20`}>{c.qty_label}</th>
                  <th className={`${th} text-right w-20`}>{c.unit_label}</th><th className={`${th} text-right w-20`}>Total</th><th className="w-5" /></tr></thead>
                <tbody>
                  {(c.rows || []).map((r, ri) => (
                    <tr key={r.id || ri} className={`border-t border-slate-100 dark:border-slate-800 ${r.include === false ? 'opacity-50' : ''}`}>
                      <td className="text-center"><input type="checkbox" checked={r.include !== false} disabled={ro} title="Include" onChange={(e) => setRow(ci, ri, { include: e.target.checked })} /></td>
                      <td className="px-0.5"><input value={r.label} disabled={ro} onChange={(e) => setRow(ci, ri, { label: e.target.value })} className={cell} /></td>
                      <td className="px-0.5"><input type="number" value={r.qty || ''} placeholder="0" disabled={ro} onChange={(e) => setRow(ci, ri, { qty: e.target.value })} className={`${cell} text-right`} /></td>
                      <td className="px-0.5"><input type="number" step="0.01" value={r.unit || ''} placeholder="0" disabled={ro} onChange={(e) => setRow(ci, ri, { unit: e.target.value })} className={`${cell} text-right`} /></td>
                      <td className="px-1.5 text-right tabular-nums text-slate-700 dark:text-slate-300">{r.include === false ? '—' : money0(num(r.qty) * (num(r.unit) + num(c.addon)))}</td>
                      <td className="text-center">{!ro && <button type="button" className="text-slate-400 hover:text-red-600" onClick={() => set(ci, { rows: c.rows.filter((_, j) => j !== ri) })}><Trash2 className="h-3 w-3" /></button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!ro && <button type="button" className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:underline"
                onClick={() => set(ci, { rows: [...(c.rows || []), { id: uid(), label: '', qty: 0, unit: 0, include: true }] })}><Plus className="h-3 w-3" /> Add row</button>}
            </div>
          );
        })}
      </div>
      {!ro && <button type="button" data-testid="budget-add-calc" className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline"
        onClick={() => onChange([...calcs, { id: uid(), name: 'New calculator', qty_label: 'Qty', unit_label: 'Cost', addon_label: '', addon: 0, source: '', line_id: null, split: [100, 0, 0, 0], rows: [] }])}>
        <Plus className="h-3 w-3" /> Add calculator</button>}
    </div>
  );
}

// ── Actuals vs budget (budget bridge — docs/AD_DASHBOARD_AND_FINANCE.md §2b-3) ──
// Each line links to OL Finance accounts (a parent includes its sub-accounts); the actual comes from the
// books as the Treasurer posts. Links can change in any status: they say where to measure, not the plan.
const INCOME_TYPES = ['Income', 'Other Income'];
const pctOf = (a, b) => (b ? `${Math.round((a * 1000) / b) / 10}%` : '—');

function Books({ b, ql, onLoadAccounts, onSaveLinks }) {
  const [acc, setAcc] = useState(null);
  const [links, setLinks] = useState({});
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [byQ, setByQ] = useState(false);
  const [pick, setPick] = useState('');
  const lines = (b.sections || []).flatMap((s) => (s.lines || []).map((l) => ({ ...l, kind: s.kind })));
  React.useEffect(() => {
    setLinks(Object.fromEntries(lines.map((l) => [l.id, l.accounts || []])));
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b.id, b.updated_at]);
  React.useEffect(() => {
    onLoadAccounts().then(setAcc).catch(() => setAcc({ linked: false, accounts: [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b.id]);
  if (!acc) return <p className="text-xs text-slate-500 p-2">Loading OL Finance accounts…</p>;
  if (!acc.linked) return <p className="text-xs text-slate-500 p-2" data-testid="budget-books-nolink">This organization has no OL Finance company yet — actuals stay typed in Line items.</p>;
  if (acc.hidden) return <p className="text-xs text-slate-500 p-2" data-testid="budget-books-hidden">The Treasurer links this budget to the books. You will see the actuals here once the Treasurer shares the Finance report viewer with you.</p>;
  const name = Object.fromEntries(acc.accounts.map((a) => [a.account_id, a.name]));
  const fam = (kind) => acc.accounts.filter((a) => (kind === 'income' ? INCOME_TYPES.includes(a.type) : !INCOME_TYPES.includes(a.type)) && a.is_active !== false);
  const sugg = Object.entries(acc.suggestions || {}).filter(([lid]) => !(links[lid] || []).length);
  const setL = (lid, ids) => { setLinks((p) => ({ ...p, [lid]: ids })); setDirty(true); };
  const save = async () => { setBusy(true); try { await onSaveLinks(links); setDirty(false); } catch (e) { /* message shown by the caller */ } finally { setBusy(false); } };
  const bk = b.books;
  const val = (l, i) => (i == null ? [num(l.actual), four(l.amounts).reduce((x, y) => x + y, 0)] : [num((l.actual_q || [])[i]), four(l.amounts)[i]]);
  const cols = byQ ? [0, 1, 2, 3] : [null];
  const head = byQ ? ql : ['Total'];
  const span = 2 + head.length * (byQ ? 2 : 4);
  const kinds = [['income', 'Income'], ['expense', 'Expenses']];
  const sumKind = (k, i) => lines.filter((l) => l.kind === k && (l.name || '').trim())
    .reduce((a, l) => { const [x, y] = val(l, i); return [a[0] + x, a[1] + y]; }, [0, 0]);
  const Cells = ({ a, bud, red }) => (
    <>
      <td className="px-1.5 text-right tabular-nums">{money0(a)}</td>
      <td className="px-1.5 text-right tabular-nums">{money0(bud)}</td>
      {!byQ && <>
        <td className={`px-1.5 text-right tabular-nums ${red ? 'text-red-600 font-semibold' : ''}`}>{money0(a - bud)}</td>
        <td className="px-1.5 text-right tabular-nums">{pctOf(a, bud)}</td></>}
    </>
  );
  return (
    <div className="space-y-2" data-testid="budget-books">
      <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-600 dark:text-slate-400">
        <span>{bk ? `Actuals from OL Finance — ${bk.company_name || acc.company_name} · ${bk.basis} basis · fiscal year ${bk.start} → ${bk.end}`
          : 'Link each line to its OL Finance account(s); actuals then come from the books as the Treasurer posts.'}</span>
        <label className="flex items-center gap-1 ml-auto"><input type="checkbox" checked={byQ} onChange={(e) => setByQ(e.target.checked)} /> By quarter</label>
        {sugg.length > 0 && (
          <button type="button" data-testid="budget-books-suggest" className="inline-flex items-center gap-1 font-medium text-blue-600 hover:underline"
            onClick={() => { setLinks((p) => ({ ...p, ...Object.fromEntries(sugg) })); setDirty(true); }}><Sparkles className="h-3 w-3" /> Suggest links ({sugg.length})</button>)}
        {dirty && <button type="button" disabled={busy} onClick={save} data-testid="budget-books-save" className="rounded bg-blue-600 px-2 py-0.5 text-white disabled:opacity-50">{busy ? 'Saving…' : 'Save links'}</button>}
      </div>
      {b.books_error && <p className="flex items-center gap-1 text-[11px] text-red-600"><AlertTriangle className="h-3 w-3" /> Could not read the actuals from OL Finance just now — the figures below are the typed ones. Try again shortly.</p>}
      {(bk?.double_linked || []).length > 0 && (
        <p className="flex items-center gap-1 text-[11px] text-amber-700 dark:text-amber-400"><AlertTriangle className="h-3 w-3" />
          Linked to more than one line (counted twice): {bk.double_linked.map((a) => name[a] || a).join(', ')}</p>)}
      <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-auto max-h-[calc(100vh-400px)]">
        <table className="w-full text-xs">
          <thead className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-800"><tr>
            <th className={`${th} text-left`}>Line item</th><th className={`${th} text-left`}>OL Finance accounts</th>
            {head.map((h) => (
              <React.Fragment key={h}>
                <th className={`${th} text-right`}>{h} actual</th><th className={`${th} text-right`}>Budget</th>
                {!byQ && <><th className={`${th} text-right`}>$ Over budget</th><th className={`${th} text-right`}>% of budget</th></>}
              </React.Fragment>))}
          </tr></thead>
          <tbody>
            {kinds.map(([k, label]) => (
              <React.Fragment key={k}>
                {(b.sections || []).filter((s) => s.kind === k).map((s) => (
                  <React.Fragment key={s.id}>
                    <tr className={k === 'income' ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-amber-50 dark:bg-amber-900/20'}>
                      <td className="px-2 py-0.5 font-semibold text-slate-800 dark:text-slate-100" colSpan={span}>{s.name}</td></tr>
                    {(s.lines || []).filter((l) => (l.name || '').trim()).map((l) => {
                      const ids = links[l.id] || [];
                      return (
                        <tr key={l.id} className={`border-t border-slate-100 dark:border-slate-800 text-slate-800 dark:text-slate-200 ${l.alert ? 'bg-red-50 dark:bg-red-900/20' : ''}`} data-testid={`books-line-${l.id}`}>
                          <td className="px-2 py-0.5 pl-4">{l.alert && <AlertTriangle className="inline h-3 w-3 mr-0.5 text-red-600" />}{l.name}</td>
                          <td className="px-1 py-0.5">
                            <div className="flex flex-wrap items-center gap-0.5">
                              {ids.map((a) => (
                                <span key={a} className="inline-flex items-center gap-0.5 rounded bg-slate-100 dark:bg-slate-700 px-1 text-[10px] text-slate-700 dark:text-slate-200">{name[a] || a}
                                  <button type="button" title="Unlink" onClick={() => setL(l.id, ids.filter((x) => x !== a))} className="text-slate-400 hover:text-red-600"><X className="h-2.5 w-2.5" /></button></span>))}
                              {pick === l.id
                                ? (
                                  <select autoFocus value="" onBlur={() => setPick('')} onChange={(e) => { if (e.target.value) setL(l.id, [...ids, e.target.value]); setPick(''); }}
                                    className="max-w-[240px] rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-1 text-[11px] text-slate-800 dark:text-slate-200">
                                    <option value="">— choose account —</option>
                                    {fam(s.kind).filter((a) => !ids.includes(a.account_id)).map((a) => <option key={a.account_id} value={a.account_id}>{a.name}</option>)}
                                  </select>)
                                : (
                                  <button type="button" onClick={() => setPick(l.id)} data-testid={`books-link-${l.id}`}
                                    className="inline-flex items-center gap-0.5 text-[10px] font-medium text-blue-600 hover:underline"><Link2 className="h-2.5 w-2.5" />{ids.length ? 'Add' : 'Link'}</button>)}
                            </div>
                          </td>
                          {cols.map((i) => { const [a, bud] = val(l, i); return <Cells key={String(i)} a={a} bud={bud} red={l.alert} />; })}
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
                <tr className="border-t-2 border-slate-300 dark:border-slate-600 font-semibold bg-slate-50/60 dark:bg-slate-800/40 text-slate-800 dark:text-slate-100">
                  <td className="px-2 py-0.5" colSpan={2}>Total {label.toLowerCase()}</td>
                  {cols.map((i) => { const [a, bud] = sumKind(k, i); return <Cells key={String(i)} a={a} bud={bud} />; })}
                </tr>
              </React.Fragment>
            ))}
            <tr className="border-t-2 border-slate-400 font-bold text-slate-900 dark:text-white">
              <td className="px-2 py-0.5" colSpan={2}>Net</td>
              {cols.map((i) => {
                const [ia, ib] = sumKind('income', i); const [ea, eb] = sumKind('expense', i);
                return <Cells key={String(i)} a={ia - ea} bud={ib - eb} />;
              })}
            </tr>
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-500 dark:text-slate-400">Flagged lines are more than {num(b.settings?.variance_alert_pct)}% past budget (expenses over, income under). Change the % in this budget's settings. The same figures are in OL Finance ▸ Reports ▸ Budgets.</p>
    </div>
  );
}

// ── Resolution ──────────────────────────────────────────────────────────────
function Resolution({ b, orgName, ro, onChange, onPrint }) {
  const [edit, setEdit] = useState(false);
  const hist = [...(b.history || [])].reverse();
  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_280px] gap-3" data-testid="budget-resolution">
      <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 max-h-[calc(100vh-360px)] overflow-auto bg-white dark:bg-slate-900/40">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs text-slate-500">The resolution the board approves. {'{org}'} {'{season}'} {'{version}'} {'{reserve_pct}'} are filled in.</span>
          {!ro && <button type="button" onClick={() => setEdit((e) => !e)} className="ml-auto text-xs font-medium text-blue-600 hover:underline">{edit ? 'Done' : 'Edit text'}</button>}
          {onPrint && <button type="button" onClick={onPrint} className={`${ro ? 'ml-auto' : ''} inline-flex items-center gap-1 text-xs font-medium text-slate-600 dark:text-slate-300 hover:underline`}><Printer className="h-3.5 w-3.5" /> Print</button>}
        </div>
        {edit && !ro
          ? <textarea rows={18} value={b.resolution || ''} onChange={(e) => onChange({ resolution: e.target.value })} className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 p-2 text-xs text-slate-900 dark:text-slate-100" />
          : <div className="whitespace-pre-wrap text-xs leading-relaxed text-slate-800 dark:text-slate-200">{renderResolution(b, orgName) || 'No resolution text yet.'}</div>}
      </div>
      <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-2 max-h-[calc(100vh-360px)] overflow-auto text-xs">
        <div className="font-semibold text-slate-800 dark:text-slate-100 mb-1">Version {b.version} · {b.status}{b.approved_on ? ` · approved ${b.approved_on}` : ''}</div>
        {hist.map((h, i) => (
          <div key={i} className="border-t border-slate-100 dark:border-slate-800 py-1 text-slate-600 dark:text-slate-400">
            <span className="font-medium text-slate-800 dark:text-slate-200">v{h.version} {h.action}</span> · {String(h.at).slice(0, 10)} · {h.by}
            {h.note && <div className="text-[11px]">{h.note}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
